/**
 * Authenticated fetch wrapper for Zeal Genesis API.
 * Automatically attaches the Cognito access token to requests.
 */
import { fetchAuthSession } from 'aws-amplify/auth';

let cachedToken: string | null = null;
let tokenExpiry = 0;

/**
 * Decode JWT payload without verification (for expiration check only).
 */
function decodeJWT(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

/**
 * Check if a token is expired or will expire in the next 5 minutes.
 */
function isTokenExpired(token: string): boolean {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) return true;
  const expiresAt = payload.exp * 1000; // Convert to milliseconds
  const now = Date.now();
  return expiresAt - now < 5 * 60 * 1000; // Expire if < 5 min left
}

/**
 * Refresh the access token using the refresh token.
 */
async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem('cognito_refresh_token');
  if (!refreshToken) return null;

  try {
    const response = await fetch('https://zealgenesis.auth.us-east-1.amazoncognito.com/oauth2/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: '4k8pla6vnreod5juntft88h7ta',
      }),
    });

    if (!response.ok) {
      console.warn('[authFetch] Token refresh failed:', response.status);
      // Clear stale tokens
      localStorage.removeItem('cognito_access_token');
      localStorage.removeItem('cognito_id_token');
      localStorage.removeItem('cognito_refresh_token');
      return null;
    }

    const data = await response.json();
    const newAccessToken = data.access_token;
    const newIdToken = data.id_token;

    // Update localStorage with fresh tokens
    localStorage.setItem('cognito_access_token', newAccessToken);
    localStorage.setItem('cognito_id_token', newIdToken);

    console.log('[authFetch] Token refreshed successfully');
    return newAccessToken;
  } catch (err) {
    console.warn('[authFetch] Token refresh error:', err);
    return null;
  }
}

async function getAccessToken(retries = 0): Promise<string | null> {
  const now = Date.now();
  // Cache token for 4 minutes (tokens last 60 min, but refresh early)
  if (cachedToken && tokenExpiry > now) {
    return cachedToken;
  }
  
  // On iOS native with manual OAuth flow, check localStorage first
  let manualToken = localStorage.getItem('cognito_access_token');
  if (manualToken) {
    // Check if token is expired
    if (isTokenExpired(manualToken)) {
      console.log('[authFetch] Token expired, attempting refresh...');
      manualToken = await refreshAccessToken();
      if (!manualToken) {
        console.warn('[authFetch] Refresh failed, user needs to re-authenticate');
        return null;
      }
    }
    cachedToken = manualToken;
    tokenExpiry = now + 4 * 60 * 1000;
    return manualToken;
  }
  
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString() || null;
    if (token) {
      cachedToken = token;
      tokenExpiry = now + 4 * 60 * 1000; // 4 min cache
    } else if (retries < 2) {
      // Token not ready yet (common after OAuth redirect) — retry after delay
      console.warn(`[authFetch] No token available, retry ${retries + 1}/2 in 1s...`);
      await new Promise(r => setTimeout(r, 1000));
      return getAccessToken(retries + 1);
    }
    return token;
  } catch {
    if (retries < 2) {
      console.warn(`[authFetch] fetchAuthSession failed, retry ${retries + 1}/2 in 1s...`);
      await new Promise(r => setTimeout(r, 1000));
      return getAccessToken(retries + 1);
    }
    cachedToken = null;
    tokenExpiry = 0;
    return null;
  }
}

/**
 * Drop-in replacement for fetch() that adds Authorization header.
 * Falls back to regular fetch if no token is available (unauthenticated endpoints).
 */
export async function authFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  const headers = new Headers(init?.headers || {});
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return fetch(input, { ...init, headers });
}

export default authFetch;
